import { new__cn as cn, createTwStyle } from '../util';
import { SLIDER_VARIANT } from './constant';
import type { VSliderProps } from './type';

export const twStyle = createTwStyle<
  Pick<VSliderProps<any>, 'disabled' | 'readOnly' | 'variant'>
>()({
  container: () =>
    cn('relative h-fit w-fit overflow-x-auto overflow-y-hidden px-0.5 pt-1'),

  filledTrack: ({ disabled, variant }) =>
    cn(
      'absolute left-0 z-1 h-full w-full rounded-md',
      'after:absolute after:left-[calc(var(--startLeft)*1px)] after:h-full after:w-[calc((var(--endLeft)-var(--startLeft))*1px)] after:translate-x-(--endX) after:rounded-md after:transition-transform after:content-[""]',
      {
        'after:bg-primary-400': variant === SLIDER_VARIANT.PRIMARY,
        'after:bg-secondary-200': variant === SLIDER_VARIANT.SECONDARY,
        'pointer-events-auto z-2 w-0 after:-top-0.5 after:h-scale/12 after:rounded-none after:bg-green-500/20 hover:after:cursor-pointer hover:after:bg-green-500/50':
          variant === SLIDER_VARIANT.FRAME_SLIDER,
        'pointer-events-auto z-2 w-0 after:top-0.5 after:h-scale/4 after:rounded-full after:bg-secondary-500/40 hover:after:cursor-pointer hover:after:bg-secondary-500/50':
          variant === SLIDER_VARIANT.FRAME_TRACK_SLIDER,
        'after:bg-gray-300':
          variant === SLIDER_VARIANT.CACHED_FRAME_TRACK_SLIDER,
      },
      {
        'after:opacity-50': disabled,
      },
    ),

  markerContainer: () =>
    cn(
      'relative -mx-0.5 h-scale/16 w-fit text-body-sm',
      '**:aria-[label=v-slider-marker]:absolute **:aria-[label=v-slider-marker]:top-0 **:aria-[label=v-slider-marker]:cursor-pointer',
      '**:aria-[label=v-slider-marker]:h-scale/16 **:aria-[label=v-slider-marker]:w-scale/16',
      '**:aria-[label=v-slider-marker]:overflow-visible',
      '**:aria-[label=v-slider-marker]:text-black **:aria-[label=v-slider-marker]:select-none',
      '**:aria-[label=v-slider-marker]:aria-disabled:cursor-not-allowed **:aria-[label=v-slider-marker]:aria-disabled:text-gray-400',
      '**:aria-[label=v-slider-marker]:aria-readonly:cursor-default **:aria-[label=v-slider-marker]:aria-readonly:text-gray-600',
      '**:aria-[label=v-slider-marker]:aria-selected:font-semibold **:aria-[label=v-slider-marker]:aria-selected:tracking-tight',
    ),

  thumb: ({ variant }) =>
    cn(
      'absolute top-px -left-px z-10 ml-0.5 box-border size-(--thumb-size) translate-x-(--tx) cursor-pointer rounded-full shadow-sm select-none',
      'aria-disabled:cursor-not-allowed aria-disabled:opacity-80',
      'aria-readonly:cursor-default aria-readonly:opacity-90',
      'before:size-1 before:rounded-full before:bg-white before:content-[""]',
      {
        'bg-primary-500': variant === SLIDER_VARIANT.PRIMARY,
        'bg-secondary-300': variant === SLIDER_VARIANT.SECONDARY,
      },
      variant === SLIDER_VARIANT.FRAME_SLIDER && [
        'top-0.5 left-[5px] h-scale/12 w-scale/2 rounded-xs bg-tertiary-500 before:content-none',
        'aria-[label=v-slider-thumb]:first:transform-[translateX(-2px)]',
        'aria-[label=v-slider-thumb]:first:not-hover:rounded-tr-none',
        'aria-[label=v-slider-thumb]:first:not-hover:rounded-br-none',
        'aria-[label=v-slider-thumb]:last:transform-[translateX(2px)]',
        'aria-[label=v-slider-thumb]:last:not-hover:rounded-tl-none',
        'aria-[label=v-slider-thumb]:last:not-hover:rounded-bl-none',
        'hover:w-1 hover:bg-secondary-300',
        'aria-pressed:w-1 aria-pressed:bg-secondary-300',
        'aria-pressed:rounded-xs',
      ],
    ),

  track: () =>
    cn(
      'relative h-scale/8 overflow-visible rounded-full bg-gray-300 px-1',
      'aria-disabled:cursor-not-allowed aria-disabled:bg-gray-200',

      '**:aria-[label=v-slider-track-marker]:absolute **:aria-[label=v-slider-track-marker]:top-0.5 **:aria-[label=v-slider-track-marker]:z-2',
      '**:aria-[label=v-slider-track-marker]:cursor-pointer **:aria-[label=v-slider-track-marker]:overflow-hidden **:aria-[label=v-slider-track-marker]:rounded-full',
      '**:aria-[label=v-slider-track-marker]:bg-white **:aria-[label=v-slider-track-marker]:select-none',
      '**:aria-[label=v-slider-track-marker]:aria-disabled:cursor-not-allowed **:aria-[label=v-slider-track-marker]:aria-disabled:bg-gray-100',
      '**:aria-[label=v-slider-track-marker]:aria-readonly:cursor-default',
    ),
});
